import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Form, Button, Table, Collapse } from 'react-bootstrap-v5';

export default function ConsultantsNotes() {
  const { id } = useParams();

  const [notesData, setNotesData] = useState({
    consultants_notes_client_relations: '',
    consultants_notes_other_notes: '',
    consultants_notes_client_feedback: ''
  });

  const [notes, setNotes] = useState([]);
  const [status, setStatus] = useState('');
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(true);

  const loadAllNotes = useCallback(async (headers) => {
    const url = `${process.env.REACT_APP_API_URL}/getallnotes/${id}`;
    try {
      const res = await axios.get(url, { headers });
      setNotes(res.data);
    } catch (err) {
      console.log("Error loading notes:", err);
    }
  }, [id]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      return;
    }

    const headers = {
      Authorization: `Bearer ${token}`,
    };

    loadAllNotes(headers);
  }, [id, loadAllNotes, editId]);

  useEffect(() => {
    if (notes.length === 0) {
      setIsFormOpen(true);
    } else {
      setIsFormOpen(false);
    }
  }, [notes.length]);

  const validateFields = () => {
    const requiredFields = {
      'Client Relations': notesData.consultants_notes_client_relations,
      'Other Notes': notesData.consultants_notes_other_notes,
      'Client Feedback': notesData.consultants_notes_client_feedback
    };

    const emptyFields = Object.entries(requiredFields)
      .filter(([_, value]) => !value)
      .map(([fieldName, _]) => fieldName);

    if (emptyFields.length > 0) {
      setError(`Please fill in all required fields: ${emptyFields.join(', ')}`);
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateFields()) return;

    const token = localStorage.getItem("token");
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    const url = `${process.env.REACT_APP_API_URL}/savenotes/${id}`;
    setStatus("saving...");
    setError("");

    try {
      const res = await axios.post(url, notesData, { headers });
      setStatus("saved");
      setError("");
      await loadAllNotes(headers);
      
      // Clear form with correct field names
      setNotesData({
        consultants_notes_client_relations: '',
        consultants_notes_other_notes: '',
        consultants_notes_client_feedback: ''
      });
    } catch (err) {
      console.log(err);
      setError(err.response?.data?.error || "error saving data");
      setStatus("");
    }
  };

  const handleUpdate = async () => {
    if (!validateFields()) return;

    const token = localStorage.getItem("token");
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    const editUrl = `${process.env.REACT_APP_API_URL}/editnotes/${id}/${editId}`;
    try {
      await axios.put(editUrl, notesData, { headers });
      
      // Clear form with correct field names
      setNotesData({
        consultants_notes_client_relations: '',
        consultants_notes_other_notes: '',
        consultants_notes_client_feedback: ''
      });
      
      setEditId(null);
      await loadAllNotes(headers);
    } catch (err) {
      console.log("Error editing notes:", err);
      setError("Error editing notes");
    }
  };

  const handleDelete = async (noteId) => {
    const token = localStorage.getItem("token");
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    const url = `${process.env.REACT_APP_API_URL}/deletenotes/${noteId}`;

    try {
      await axios.delete(url, { headers });
      loadAllNotes(headers);
    } catch (err) {
      console.log("Error deleting note:", err);
    }
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="container">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-end mb-3">
            <Button 
              onClick={() => setIsFormOpen(!isFormOpen)}
              variant="outline-primary"
              size="sm"
            >
              {isFormOpen ? 'Hide Form' : 'Add Note'}
            </Button>
          </div>

          <Collapse in={isFormOpen}>
            <div>
              <div className="row">
                <div className="col-md-12">
                  <Form.Group className="mb-3">
                    <Form.Label>1. Client Relations / Refund Requests</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={notesData.consultants_notes_client_relations}
                      onChange={(e) =>
                        setNotesData({ ...notesData, consultants_notes_client_relations: e.target.value })
                      }
                      required
                    />
                  </Form.Group>
                </div>

                <div className="col-md-12">
                  <Form.Group className="mb-3">
                    <Form.Label>2. Other Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={notesData.consultants_notes_other_notes}
                      onChange={(e) =>
                        setNotesData({ ...notesData, consultants_notes_other_notes: e.target.value })
                      }
                      required
                    />
                  </Form.Group>
                </div>

                <div className="col-md-12">
                  <Form.Group className="mb-3">
                    <Form.Label>3. Client Feedback On Matches</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={notesData.consultants_notes_client_feedback}
                      onChange={(e) =>
                        setNotesData({ ...notesData, consultants_notes_client_feedback: e.target.value })
                      }
                      required
                    />
                  </Form.Group>
                </div>

                <div className="col-md-12">
                  {editId ? 
                    <Button onClick={handleUpdate} style={{ marginTop: "20px" }}>Update</Button> : 
                    <Button onClick={handleSave} style={{ marginTop: "20px" }}>Save</Button>
                  }
                  <div className="status">{status}</div>
                  <div className="error" style={{ color: 'red' }}>{error}</div>
                </div>
              </div>
            </div>
          </Collapse>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <h2>All Notes</h2>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Created At</th>
                <th>Updated At</th>
                <th>Client Relations</th>
                <th>Other Notes</th>
                <th>Client Feedback</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {notes.map((note) => (
                <tr key={note.id}>
                  <td>{formatDate(note.created_at)}</td>
                  <td>{formatDate(note.updated_at)}</td>
                  <td>{note.consultants_notes_client_relations}</td>
                  <td>{note.consultants_notes_other_notes}</td>
                  <td>{note.consultants_notes_client_feedback}</td>
                  <td>
                    <Button  className="btn btn-outline-primary btn-sm me-2" onClick={() => {
                      setEditId(note.id);
                      setIsFormOpen(true);
                      setNotesData({
                        consultants_notes_client_relations: note.consultants_notes_client_relations || '',
                        consultants_notes_other_notes: note.consultants_notes_other_notes || '',
                        consultants_notes_client_feedback: note.consultants_notes_client_feedback || ''
                      });
                    }}  >
                      Edit
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => handleDelete(note.id)}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  );
}
